import { AppNode } from '../types';
import { CoordinateUtils, BoundingBox } from './coordinateUtils';

export const GROUP_PADDING = 40;
export const GROUP_MIN_WIDTH = 200;
export const GROUP_MIN_HEIGHT = 150;
export const GROUP_HEADER_HEIGHT = 40;
export const EXIT_THRESHOLD = 0.5;

export function getNodesInBounds(
  nodes: AppNode[],
  bounds: BoundingBox,
  strict: boolean = false
): AppNode[] {
  return nodes.filter(node => {
    const nodeBounds = CoordinateUtils.getNodeBounds(node);
    if (strict) {
      return CoordinateUtils.isRectContains(bounds, nodeBounds);
    }
    return CoordinateUtils.isRectIntersect(bounds, nodeBounds);
  });
}

export function calculateGroupBounds(
  nodes: AppNode[],
  padding: number = GROUP_PADDING
): BoundingBox {
  return CoordinateUtils.calculateBounds(nodes, padding);
}

export function calculateGroupBoundsFromAbsolute(
  nodes: AppNode[],
  groupNode: AppNode,
  padding: number = GROUP_PADDING
): BoundingBox {
  if (nodes.length === 0) {
    return {
      x: groupNode.x,
      y: groupNode.y,
      width: GROUP_MIN_WIDTH,
      height: GROUP_MIN_HEIGHT,
    };
  }

  const absoluteNodes = nodes.map(n => ({
    ...n,
    x: n.x + groupNode.x,
    y: n.y + groupNode.y,
  }));

  return CoordinateUtils.calculateBounds(absoluteNodes, padding);
}

export function getChildNodeIds(
  nodes: AppNode[],
  groupId: string
): string[] {
  return nodes
    .filter(node => node.groupId === groupId || node.parentNode === groupId)
    .map(node => node.id);
}

export function getGroupNodes(
  nodes: AppNode[],
  groupId: string
): AppNode[] {
  return nodes.filter(node => node.groupId === groupId || node.parentNode === groupId);
}

export function isNodeInGroup(
  node: AppNode,
  groupId: string
): boolean {
  return node.groupId === groupId || node.parentNode === groupId;
}

export function findGroupAtPosition(
  nodes: AppNode[],
  groupIds: string[],
  position: { x: number; y: number }
): string | null {
  for (const groupId of groupIds) {
    const group = nodes.find(n => n.id === groupId);
    if (!group) continue;

    const bounds: BoundingBox = {
      x: group.x,
      y: group.y,
      width: group.width || 400,
      height: group.height || 300,
    };

    if (CoordinateUtils.isPointInBounds(position, bounds)) {
      return groupId;
    }
  }
  return null;
}

export function generateGroupId(): string {
  return `group-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

export function getDefaultGroupTitle(index: number): string {
  return `组 ${index + 1}`;
}

export function isNodeCenterInsideGroup(
  node: AppNode,
  groupNode: AppNode,
  buffer: number = 0
): boolean {
  const nodeCenter = CoordinateUtils.getNodeCenter(node);

  const groupBounds: BoundingBox = {
    x: groupNode.x,
    y: groupNode.y,
    width: groupNode.width || 400,
    height: groupNode.height || 300,
  };

  return CoordinateUtils.isPointInBounds(nodeCenter, groupBounds, buffer);
}

export function calculateExitProgress(
  nodeBounds: BoundingBox,
  groupBounds: BoundingBox
): number {
  if (!CoordinateUtils.isRectIntersect(nodeBounds, groupBounds)) {
    return 1;
  }
  if (CoordinateUtils.isRectContains(groupBounds, nodeBounds)) {
    return 0;
  }

  const overlapX = Math.max(
    0,
    Math.min(nodeBounds.x + nodeBounds.width, groupBounds.x + groupBounds.width) -
    Math.max(nodeBounds.x, groupBounds.x)
  );
  const overlapY = Math.max(
    0,
    Math.min(nodeBounds.y + nodeBounds.height, groupBounds.y + groupBounds.height) -
    Math.max(nodeBounds.y, groupBounds.y)
  );

  const overlapArea = overlapX * overlapY;
  const nodeArea = nodeBounds.width * nodeBounds.height;

  if (nodeArea === 0) return 1;

  return 1 - overlapArea / nodeArea;
}
